import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { EASE } from "@/components/Reveal";

const LINKS = [
  { to: "/", label: "Inicio", testid: "nav-link-home" },
  { to: "/transporte", label: "Transporte", testid: "nav-link-transporte" },
  { to: "/logistica-deportiva", label: "Logística Deportiva", testid: "nav-link-logistica" },
  { to: "/protocolo", label: "Protocolo", testid: "nav-link-protocolo" },
  { to: "/ciudad-garaje", label: "Ciudad Garaje", testid: "nav-link-ciudad-garaje" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
          scrolled ? "border-b border-line bg-void/80 backdrop-blur-xl" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="nav-brand-logo" className="group flex items-baseline gap-2">
            <span className="font-display text-sm font-bold tracking-[0.35em] text-slate-100">
              GRUPO
            </span>
            <span className="font-serif text-2xl font-bold text-gold-gradient">Disimil</span>
          </Link>

          <nav className="hidden items-center gap-8 lg:flex">
            {LINKS.slice(1).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-slate-400 hover:text-slate-100"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href="mailto:contacto@grupodisimil.com"
              data-testid="nav-cta-contact"
              className="group inline-flex items-center gap-2 border border-gold/40 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] text-gold transition-colors duration-300 hover:bg-gold hover:text-void"
            >
              Contacto
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </nav>

          <button
            className="text-slate-200 lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Abrir menú"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-40 flex flex-col justify-center bg-void/95 px-8 backdrop-blur-2xl lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            data-testid="nav-mobile-menu"
          >
            {LINKS.map((l, i) => (
              <motion.div
                key={l.to}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 + i * 0.07, duration: 0.6, ease: EASE }}
              >
                <NavLink
                  to={l.to}
                  data-testid={`${l.testid}-mobile`}
                  className={({ isActive }) =>
                    `block border-b border-line py-5 font-serif text-4xl ${
                      isActive ? "text-gold" : "text-slate-200"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
